import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AnalyticsBloc } from '@kanalytics';
import { HeartbeatApi } from '@kapi';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { GroupValidationBloc } from '@kp/auth/group-validation.bloc';
import { OnboardingBloc } from '@kp/onboarding/onboarding.bloc';
import { UserBloc } from '@kp/user/user.bloc';
import { AuthDataService, BrowserStorage, DataStoreService } from '@kservice';
import { PaymentRequiredStatusMessage, UserType } from '@ktypes/enums';
import { OnboardingData } from '@ktypes/models';
import { EnumUtil } from '@kutil';
import { Observable, combineLatest, of, withLatestFrom } from 'rxjs';
import { catchError, filter, mergeMap, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthenticatedGuard {
  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private _authDataService: AuthDataService,
    private _authenticationBloc: AuthenticationBloc,
    private _browserStorage: BrowserStorage,
    private _dataStoreService: DataStoreService,
    private _groupValidationBloc: GroupValidationBloc,
    private _heartbeatApi: HeartbeatApi,
    private _onboardingBloc: OnboardingBloc,
    private _router: Router,
    private _userBloc: UserBloc
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const savedOnboardingData = this._browserStorage.getObject('onboarding') as OnboardingData;

    return combineLatest([this._userBloc.fetchingUser$, this._dataStoreService.user$]).pipe(
      filter(
        ([fetchingUser, user]) => !fetchingUser?.data && ((!user || user?.hasLoadedData || user?.hasNoData) as boolean)
      ),
      withLatestFrom(this._groupValidationBloc.groupValidationDataStatus$),
      withLatestFrom(this._authenticationBloc.currentPaymentRequiredErrorMessage$),
      mergeMap(([[[_, user], groupValidationStatus], paymentRequiredErrorMessage]) => {
        if (!user || user.error) {
          void this._router.navigate(['/error/update']);
        }
        // If the user manually navigates to any of the authenticated routes but is onboarding
        if (
          !groupValidationStatus &&
          !paymentRequiredErrorMessage &&
          !this._authDataService.validateUserType(UserType.user) &&
          savedOnboardingData &&
          savedOnboardingData.groupCode
        ) {
          this._onboardingBloc.restoreSavedOnboardingValues(savedOnboardingData);
          void this._router.navigate([`/${savedOnboardingData.latestPage}`], { replaceUrl: true });
          return of(false);
        }

        if (paymentRequiredErrorMessage) {
          if (EnumUtil.getByValue(PaymentRequiredStatusMessage, paymentRequiredErrorMessage)) {
            return of(true);
          }
          this._authenticationBloc.logout(false);
          void this._router.navigate(['/login'], {
            queryParams: {
              redirectUrl: encodeURIComponent(state.url),
            },
          });
          return of(false);
        }

        if (this._authenticationBloc.isLoggedIn()) {
          // no need to check isTokenExpiredLocally() as the heartbeat call will try to auto-refresh the token if expired
          return this._heartbeatApi.isValid$(true).pipe(
            tap((isValid) => {
              if (isValid) {
                // don't mark user ready until we get a valid heartbeat signal
                this._analyticsBloc.userReadyForAnalytics();
              } else {
                console.error('logged in by invalid credentials');
                this._handleAuthError(new Error('authentication is invalid'), state);
              }
            }),
            catchError((err) => this._handleAuthError(err, state))
          );
        }

        void this._router.navigate(['/login'], {
          queryParams: {
            redirectUrl: encodeURIComponent(state.url),
          },
        });
        return of(false);
      })
    );
  }

  private _handleAuthError(err: any, state: RouterStateSnapshot) {
    if (err != null) {
      console.warn(err);
    }
    this._authenticationBloc.logout(false);
    void this._router.navigate(['/login'], {
      queryParams: {
        redirectUrl: encodeURIComponent(state.url),
      },
    });
    return of(false);
  }
}
